import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@rakazo/ui-web";
import { ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { useSpaceRpc } from "../lib/space-rpc";
import { downloadIdentityJson } from "./AgentIdentitySettings";
import type { AgentIdentityProofResult } from "@rakazo/contracts";
import type { z } from "zod";
export function MessageSignatureButton({
  source,
  messageId,
}: {
  source: "thread" | "channel";
  messageId: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        type="button"
        size="icon-sm"
        variant="ghost"
        aria-label="Verify signed text"
        onClick={() => setOpen(true)}
      >
        <ShieldCheck size={15} />
      </Button>
      <MessageSignatureDialog
        source={source}
        messageId={messageId}
        open={open}
        onOpenChange={setOpen}
      />
    </>
  );
}

export function MessageSignatureDialog({
  source,
  messageId,
  open,
  onOpenChange,
}: {
  source: "thread" | "channel";
  messageId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const rpc = useSpaceRpc();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<z.infer<typeof AgentIdentityProofResult> | null>(null);
  useEffect(() => {
    if (!open) return;
    let current = true;
    setBusy(true);
    setResult(null);
    setError("");
    void rpc.agentIdentities
      .proof({ source, messageId })
      .then((value) => {
        if (current) setResult(value);
      })
      .catch((error) => {
        if (current) setError(error instanceof Error ? error.message : "Signature unavailable.");
      })
      .finally(() => {
        if (current) setBusy(false);
      });
    return () => {
      current = false;
    };
  }, [open, source, messageId, rpc]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Signed text</DialogTitle>
          <DialogDescription>
            Verifies the text and its conversation ID. Images, attachments and tool output are
            outside this signature.
          </DialogDescription>
        </DialogHeader>
        {busy ? (
          <p className="text-sm">Checking signature…</p>
        ) : error ? (
          <p className="text-sm" role="status">
            {error}
          </p>
        ) : result ? (
          <>
            <p
              className={
                result.verification.valid ? "text-sm text-success" : "text-sm text-destructive"
              }
            >
              {result.verification.valid ? "Signature verified" : "Signature does not match"}
              {result.keyStatus === "previous" ? " · previous key" : ""}
            </p>
            <p className="break-all font-mono text-xs">{result.npub}</p>
            <p className="text-xs text-muted-foreground">
              The server associates this key with the bot. External verification requires an
              independently trusted public key.
            </p>
            <Button
              type="button"
              variant="outline"
              onClick={() => downloadIdentityJson(result.proof, "wurkees-signed-text.json")}
            >
              Download proof
            </Button>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
